import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '../ns-form/ns-form.js';
import {ADDRESS_TRANSLATIONS} from './address-translations.js';
import template from './ns-address.html';
import {AddressFormatterFactory} from './addressFormatter.js';
import {AutoCompleteAddress} from './autoCompleteAddress.js';
import {ADDRESS_CONFIG} from './address-config.js';
import {FormMixin} from '../formMixin.js';
import {GoogleMapsAPILoader} from '@nuskin/ns-util';

/**
 * Displays a form with inputs to capture a user's address.
 * Address form values are data-bound to other components to pass
 * values to any components expecting it.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Address Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-address type="billing"></ns-address>
 *   </template>
 * </demo-snippet>
 */
export class NsAddress extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-address';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * String. Used for localization.
             */
            country: {
                type: String,
                value: "US",
                observer: '_initialize'
            },
            /**
             * String. Used for localization.
             */
            language: {
                type: String,
                value: "en",
                observer: '_initialize'
            },
            /**
             * Autocomplete object bound from ns-input. Contains an observer that autocompletes
             * the ns-address form upon selection for Google API.
             */
            addressAutocomplete: {
                type: Object,
                observer: 'onPlaceChanged'
            },
            /**
             * Stores value for addressLine1.
             * Notify true set for data-binding with other components
             */
            addressLine1: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Stores value for addressLine2.
             * Notify true set for data-binding with other components
             */
            addressLine2: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Stores value for city input.
             * Notify true set for data-binding with other components
             */
            city: {
                type: String,
                notify: true
            },
            /**
             * Boolean value used to display or not display the type header.
             */
            hasTypeHeader: {
                type: Boolean,
                value: false
            },
            /**
             * Comes from AEM configuration. Hides the secondary shipping address on the ADR manage shipping section.
             */
            hideSecondaryAddress: {
                type: Boolean,
                value: false
            },

            /**
             * Enable google auto complete for the address
             */
            enableGoogleAutoComplete: {
                type: Boolean,
                value: false
            },

            /**
             * Query string that specifies the autocomplete library to use. This is already set in the component.
             */
            queryString: {
                type: String,
                value: '&libraries=places&onload=callback'
            },
            /**
             * stores value of county input
             */
            county: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Stores value for state input.
             * Notifies true set for data-binding with other components
             */
            state: {
                type: String,
                notify: true
            },
            /**
             * String. Address type, i.e. 'billing' or 'shipping', use to display header
             */
            type: {
                type: String,
                value: 'shipping'
            },
            /**
             * Stores value for zip input
             * Notify true set for data-binding with other components
             */
            zip: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Shows the city state zip section before address line 1.
             */
            showCityStateZipFirst: {
                type: Boolean,
                value: false
            },
            /**
             * Hides addressLine2
             */
            hideAddressLine2: {
                type: Boolean,
                value: false
            },
            /**
             * Config for address line 1 not required
             */
            optionalAddress1: {
                type: Boolean,
                value: false
            },
            validationDisabled: {
                type: Boolean,
                value: false
            },
            sortStateList: {
                type: Boolean,
                value: true
            },
            /**
             * Translations from the server. Triggers the translation assignment process.
             */
             serverTranslations: {
                type: Object,
                value: function() {
                    return {}
                }
            },
            useServerTranslations: {
                type: Boolean,
                value: false
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(addressLine1Dirty, addressLine2Dirty, cityStateZipDirty)'
        ];
    }

    ready() {
        super.ready();
        this._initialize();
    }

    /**
     * Called if the country or langauge changes and on first load
     * @private
     */
    _initialize() {
        if (this.country && this.country.length === 2 && this.language && this.language.length === 2) {
            this._localize(this.language, this.country, ADDRESS_TRANSLATIONS, this.serverTranslations, this.useServerTranslations);
            this._setConfig(this.country, ADDRESS_CONFIG);
            this.addressFormatter = AddressFormatterFactory.getAddressFormatter(this.country);
            this._googleApiEnable();
        }
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        if (this.validationDisabled) {
            return true;
        }
        return this.shadowRoot.querySelector('ns-form').validate();
    }

    /**
     * After a selection from the list of addresses is made, assigns address
     * values to form elements.
     * @param {*} clickEvent
     */
    onPlaceChanged(clickEvent) {
        this.addressLine1 = clickEvent['formatted_address'];
        let addressComponents = clickEvent['address_components'];
        if (addressComponents === undefined) {
            return;
        }
        this.setPlaceFields(addressComponents);
        this.validate();
    }

    /**
     * Sends place data to appropriate input fields in the component.
     * @param {*} addressFields
     * @param {*} addressComponents
     */
    setPlaceFields(addressComponents) {
        var newAddress = new AutoCompleteAddress(addressComponents);
        let autocompleteAddress = this.addressFormatter.formatAutoCompleteAddress(newAddress);

        this.addressLine1 = autocompleteAddress.streetAddress;
        this.addressLine2 = autocompleteAddress.streetAddress2;
        this.city = autocompleteAddress.city;
        this.state = autocompleteAddress.state;
        this.zip = autocompleteAddress.zipcode;

        /*if (this.country === 'HK') {
          this.state = this.serverTranslations.marketSelectHK;
        } else if (this.country === 'MO') {
          this.state = this.serverTranslations.marketSelectMO;
        }
        */
    }

    /**
     * Enables the Google Address Api on Ns-Address.
     * If the library script hasn't been loaded, it attaches it to the head tag,
     * otherwise it'll attach the places api to the input right away.
     */
    _googleApiEnable() {
        if (this.enableGoogleAutoComplete) {
            GoogleMapsAPILoader.load()
            .then(() => this._loadSrc())
            .catch(() => {
                console.error("Failed to load Google Maps JavaScript API");
            });
        }
    }

    /**
     * Checks for the first address input and initializes the Autocomplete function with it
     */
    _loadSrc() {
        let addressLineInput = this.$.addressLine1.shadowRoot.querySelector('#input');
        if (addressLineInput.name === 'addressLine1') {
            if (typeof (google) === 'undefined') {
                return;
            }
            // Autocomplete options object - restrict results by country
            let options = {
                componentRestrictions: {country: this.country}
            };
            this.autocomplete = new google.maps.places.Autocomplete(addressLineInput, options);
            google.maps.event.addListener(this.autocomplete, 'place_changed', () => {
                this.addressAutocomplete = this.autocomplete.getPlace();
            });
        }
    }
}

customElements.define(NsAddress.is, NsAddress);
